"use client";

import { useState, useEffect } from "react";
import Link from "@/components/Link";
import {
  ActionIcon,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
} from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import ItemCarousel from "@/components/ItemCarousel";
//import FollowButton from "@/components/FollowButton";
import { loadItems } from "@/lib/client/api";
import { useFallbackProductImage } from "@/hooks/useFallbackProductImage";
import { generateBrandSlug, generateVendorSlug } from "@/lib/shared/utils";
import Globe from "@/icons/Globe";
import InstagramOutline from "@/icons/InstagramOutline";
import { SlideIn } from "@/components/SlideIn";
import { sendGtagEvent } from "@/lib/client/gtag";

import styles from "./styles.module.scss";

export interface EntitySlideInData {
  id: number;
  label: string;
  description?: string | null;
  imageUrl?: string | null;
  website?: string | null;
  instagram?: string | null;
  subtitle?: string | null;
  doesRenting?: boolean | null;
  region?: string | null;
}

interface EntitySlideInProps {
  active: boolean;
  data?: EntitySlideInData;
  handleClose: () => void;
  type: "brand" | "vendor";
  searchParamKey: "brand" | "vendor";
}

const EntitySlideIn = ({ active, data, handleClose, type, searchParamKey }: EntitySlideInProps) => {
  const [items, setItems] = useState<SearchResultItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  // Reset image states when entity changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [data?.id]);

  const fallbackImage = useFallbackProductImage({
    type,
    id: data?.id,
    label: data?.label,
    primaryImageUrl: data?.imageUrl,
    primaryImageError: imageError,
    enabled: active,
  });

  useEffect(() => {
    setHasAnimatedIn(false);
  }, [data?.id]);

  useEffect(() => {
    if (!active || !data?.id) {
      setItems([]);
      return;
    }

    let cancelled = false;
    setItems([]);
    setLoadingItems(true);

    const fetchItems = async () => {
      try {
        const query =
          type === "brand"
            ? { brands: [{ id: data.id, value: data.id, label: data.label, included: true }] }
            : { vendors: [{ id: data.id, value: data.id, label: data.label, included: true }] };

        const result = await loadItems({ query, limit: 20, offset: 0 });
        if (!cancelled) setItems(result?.items ?? []);
      } catch (error) {
        console.error(`Error loading ${type} items:`, error);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    };

    fetchItems();
    return () => {
      cancelled = true;
    };
  }, [active, data, type]);

  useEffect(() => {
    if (active && !hasAnimatedIn) {
      const timer = setTimeout(() => setHasAnimatedIn(true), 220);
      return () => clearTimeout(timer);
    }
  }, [active, hasAnimatedIn]);

  const initial = data?.label.charAt(0).toUpperCase() || "-";
  const usingFallback = imageError || !data?.imageUrl;
  const displayImage = usingFallback ? fallbackImage : data?.imageUrl;
  const hasImage = !!displayImage;
  const showInitial = !hasImage || (!imageLoaded && !usingFallback);

  return (
    <SlideIn
      active={active}
      onClose={handleClose}
      side="right"
      width="narrow"
      offsetTop={false}
      overflow="auto"
      padding="mobile"
      animate={active && !hasAnimatedIn}
      className={styles.slideIn}
      header={
        <MantineBox className={styles.closeContainer}>
          <MantineBox className={styles.titleInHeader}>
            <MantineBox className={styles.entityNameRow}>
              <Link
                href={
                  type === "brand"
                    ? `/brands/${generateBrandSlug(data?.label || "")}`
                    : `/vendors/${generateVendorSlug(data?.label || "")}`
                }
                className={styles.entityName}
                onClick={handleClose}
              >
                {data?.label}
              </Link>
              {/* <FollowButton
                type={type}
                id={data?.id}
                size="small"
                brand={type === "brand" ? (data as any) : undefined}
                vendor={type === "vendor" ? (data as any) : undefined}
              /> */}
            </MantineBox>
            {data?.subtitle && (
              <MantineText span className={styles.entitySubtitle}>
                {data.subtitle}
              </MantineText>
            )}
          </MantineBox>
          <ActionIcon onClick={handleClose} variant="subtle" size={36} radius="sm" color="black">
            <IconX size={24} style={{ color: "black" }} />
          </ActionIcon>
        </MantineBox>
      }
    >
      <MantineBox className={styles.entityContent}>
        <MantineBox className={styles.entityHeader}>
          <MantineBox className={styles.entityImagePlaceholder}>
            <MantineText
              span
              className={styles.entityInitial}
              style={{ opacity: showInitial ? 1 : 0 }}
            >
              {initial}
            </MantineText>
            {hasImage && (
              <MantineImage
                src={displayImage!}
                alt={data?.label || ""}
                className={styles.entityLogoImage}
                style={{ opacity: usingFallback || imageLoaded ? 1 : 0 }}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            )}
          </MantineBox>
          <MantineBox className={styles.entityHeaderInfo}>
            {data?.description && (
              <MantineText className={styles.description}>{data.description}</MantineText>
            )}

            <MantineBox className={styles.entityLinks}>
              {data?.website && (
                <MantineAnchor
                  className={styles.entityLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={data.website}
                  aria-label="Visit website"
                  onClick={() =>
                    sendGtagEvent(`${type}_website_click`, "outbound", data.label ?? "", { type })
                  }
                >
                  <Globe size={24} />
                </MantineAnchor>
              )}
              {data?.instagram && (
                <MantineAnchor
                  className={styles.entityLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`https://instagram.com/${data.instagram}`}
                  aria-label="Visit Instagram"
                  onClick={() =>
                    sendGtagEvent(`${type}_instagram_click`, "outbound", data.label ?? "", { type })
                  }
                >
                  <InstagramOutline size={24} />
                </MantineAnchor>
              )}
              {type === "vendor" && data?.doesRenting && (
                <MantineBox className={styles.rentalBadge}>
                  <MantineText span>Offers Rentals</MantineText>
                </MantineBox>
              )}
            </MantineBox>
          </MantineBox>
        </MantineBox>

        {type === "vendor" && (data?.subtitle || data?.region) && (
          <MantineBox className={styles.vendorMeta}>
            {data.subtitle && (
              <MantineText span className={styles.metaChip}>
                {data.subtitle}
              </MantineText>
            )}
            {data.region && (
              <MantineText span className={styles.metaChip}>
                {data.region}
              </MantineText>
            )}
          </MantineBox>
        )}

        {data?.id && (
          <Link
            prefetch={false}
            className={styles.actionButton}
            href={`/search?${searchParamKey}=${encodeURIComponent(data.label || "")}`}
          >
            {type === "brand"
              ? `SHOP ${data.label?.toUpperCase()}`
              : `BUY MORE FROM ${data.label?.toUpperCase()}`}
          </Link>
        )}

        <MantineBox className={styles.latestItemsSection}>
          <ItemCarousel
            items={items}
            loading={loadingItems}
            ghosts={8}
            routeToProductPage
            edgeToEdge
            title={
              type === "brand"
                ? `LATEST ${data?.label.toUpperCase()} ITEMS`
                : `${data?.label.toUpperCase()} LATEST ITEMS`
            }
          />
        </MantineBox>
      </MantineBox>
    </SlideIn>
  );
};

export default EntitySlideIn;
